/**
 * Reading a selected file's bytes, and encoding them for a JSON body.
 *
 * Two screens needed the same thing and each had grown its own: the branding
 * page read a File through `FileReader.readAsDataURL` and sliced off the data
 * prefix, while the photo upload needed base64 of bytes it had *already* read
 * for hashing and EXIF. Encoding lives here once; reading a File is expressed
 * in terms of it, so a caller that already holds the bytes never reads twice.
 */

/**
 * Read a Blob/File into an ArrayBuffer. Uses the native `Blob.arrayBuffer()`
 * when present (all modern browsers) and falls back to `FileReader` for
 * environments that omit it.
 */
export function readArrayBuffer(file: Blob): Promise<ArrayBuffer> {
  if (typeof file.arrayBuffer === 'function') return file.arrayBuffer();
  return new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () =>
      reject(reader.error ?? new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
}

/**
 * Bytes to base64. Chunked because spreading a large array into
 * `String.fromCharCode` overflows the call stack.
 */
export function toBase64(bytes: ArrayBuffer): string {
  const view = new Uint8Array(bytes);
  let binary = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < view.length; i += CHUNK) {
    binary += String.fromCharCode(...view.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

/** A File straight to base64, for callers that do not need the raw bytes. */
export async function fileToBase64(file: Blob): Promise<string> {
  return toBase64(await readArrayBuffer(file));
}
